import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  useSettings,
  GeneralSettings,
  SecuritySettings,
  NotificationSettings,
} from '../features/settings';

export function Settings() {
  const { t } = useTranslation();
  const {
    settings,
    isLoading,
    error,
    fetchSettings,
    updateGeneralSettings,
    updateSecuritySettings,
    updateNotificationSettings,
  } = useSettings();

  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleGeneralSave = async (updates: Parameters<typeof updateGeneralSettings>[0]) => {
    try {
      const result = await updateGeneralSettings(updates);
      if (result) {
        setActionMessage({ type: 'success', text: t('settings.saved') });
        setTimeout(() => setActionMessage(null), 3000);
      }
      return result;
    } catch {
      setActionMessage({ type: 'error', text: t('settings.saveFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const handleSecuritySave = async (updates: Parameters<typeof updateSecuritySettings>[0]) => {
    try {
      const result = await updateSecuritySettings(updates);
      if (result) {
        setActionMessage({ type: 'success', text: t('settings.saved') });
        setTimeout(() => setActionMessage(null), 3000);
      }
      return result;
    } catch {
      setActionMessage({ type: 'error', text: t('settings.saveFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const handleNotificationSave = async (
    updates: Parameters<typeof updateNotificationSettings>[0]
  ) => {
    try {
      const result = await updateNotificationSettings(updates);
      if (result) {
        setActionMessage({ type: 'success', text: t('settings.saved') });
        setTimeout(() => setActionMessage(null), 3000);
      }
      return result;
    } catch {
      setActionMessage({ type: 'error', text: t('settings.saveFailed') });
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  if (isLoading && !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      {actionMessage && (
        <div
          className={`p-4 rounded-lg ${
            actionMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">{error}</div>
      )}

      <div className="grid gap-6">
        <GeneralSettings
          settings={settings?.general || null}
          isLoading={isLoading}
          onSave={handleGeneralSave}
        />

        <SecuritySettings
          settings={settings?.security || null}
          isLoading={isLoading}
          onSave={handleSecuritySave}
        />

        <NotificationSettings
          settings={settings?.notifications || null}
          isLoading={isLoading}
          onSave={handleNotificationSave}
        />

        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">{t('settings.dangerZone')}</CardTitle>
            <CardDescription>{t('settings.dangerZoneDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-destructive/50">
              <div>
                <p className="font-medium">{t('settings.deleteOrganization')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('settings.deleteOrganizationDescription')}
                </p>
              </div>
              <Button variant="destructive">{t('settings.deleteOrganization')}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
